import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ArrowDownIcon } from 'icons/arrow-down';
import { SearchIcon } from 'icons/search-icon';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Skeleton } from './skeleton';

export interface PaginatedDropdownProps {
  label: string;
  type: string;
  value: string;
  options: ReadonlyArray<{ key: string; label: string }>;
  onSelect: (type: string, option: { key: string; label: string }) => void;
  onSearch?: (query: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
  searchable?: boolean;
}

export function PaginatedDropdown({
  label,
  value,
  options,
  type,
  onSelect,
  onSearch,
  onLoadMore,
  hasMore = false,
  isLoading = false,
  searchable = true,
}: PaginatedDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const debouncedOnSearch = useMemo(
    () =>
      debounce((query: string) => {
        onSearch?.(query);
      }, 300),
    [onSearch],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const query = e.target.value;
      setSearchQuery(query);
      setIsSearching(true);
      debouncedOnSearch(query);
    },
    [debouncedOnSearch],
  );

  const handleScroll = useMemo(
    () =>
      throttle(() => {
        if (!scrollRef.current || !hasMore || isLoading) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;

        if (isNearBottom) {
          setIsLoadingMore(true);
          onLoadMore?.();
        }
      }, 100),
    [hasMore, isLoading, onLoadMore],
  );

  // Autofocus the search input when the dropdown is opened
  useEffect(() => {
    if (isOpen && searchable) {
      const timeoutId = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [isOpen, searchable]);

  useEffect(() => {
    if (!isOpen) {
      setIsSearching(false);
      setIsLoadingMore(false);
      // Don't reset search query to prevent unnecessary API calls
      // debouncedOnSearch('');
    }
  }, [isOpen, debouncedOnSearch]);

  useEffect(() => {
    if (!isLoading) {
      setIsSearching(false);
      setIsLoadingMore(false);
    }
  }, [isLoading]);

  useEffect(() => {
    return () => {
      debouncedOnSearch.cancel();
      handleScroll.cancel();
    };
  }, [debouncedOnSearch, handleScroll]);

  const renderSkeletons = (numberOfSkeletons: number) => {
    return Array.from({ length: numberOfSkeletons }).map((_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <div key={`skeleton-${index}`} className="px-4 py-3">
        <Skeleton className="h-4 w-full" />
      </div>
    ));
  };

  const renderOptions = () => {
    if (options.length === 0 && !isLoading) {
      return (
        <div className="text-14 px-4 py-3 text-gray-500">No options found</div>
      );
    }
    return options.map(option => (
      <DropdownMenu.Item
        key={option.key}
        className="text-14 focus:bg-primary-50 hover:bg-primary-50 cursor-pointer px-4 py-3 leading-4 outline-none"
        onSelect={() => {
          onSelect(type, option);
          setIsOpen(false);
        }}
      >
        {option.label}
      </DropdownMenu.Item>
    ));
  };

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger className="flex h-8 w-full items-center gap-2 rounded-3xl bg-gray-100 px-3 py-2 outline-none md:w-fit">
        <p className="text-14 leading-4">
          {label}: <b>{value}</b>
        </p>
        <ArrowDownIcon />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={5}
          className="z-1000 rounded-6 flex max-h-[400px] min-w-[240px] cursor-default flex-col overflow-hidden border border-solid border-gray-100 bg-white py-2 shadow-md"
          onCloseAutoFocus={e => e.preventDefault()}
          loop={false}
        >
          {searchable && (
            <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-2">
              <SearchIcon className="h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="text-14 flex-1 bg-transparent outline-none"
              />
            </div>
          )}
          <div
            ref={scrollRef}
            className="flex-1 overflow-auto"
            onScroll={handleScroll}
          >
            {isSearching ? (
              renderSkeletons(5)
            ) : (
              <>
                {renderOptions()}
                {(isLoadingMore || isLoading) && renderSkeletons(1)}
              </>
            )}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
